/**
 * ThreadReaderSurface — the reader's full-bleed chrome, shared by both lanes.
 *
 *   ┌───────────────────────────────────────────────────────────┐
 *   │ toolbar (fixed)                                           │
 *   ├───────────────────────────────────────────────────────────┤
 *   │ subject                                              ★    │  ← scrolls
 *   │ [Google] [External] [support@…]   3 messages in this thread│
 *   │ ───────────────────────────────────────────────────────── │
 *   │ messages …                                                │
 *   ├───────────────────────────────────────────────────────────┤
 *   │ footer (fixed)                                            │
 *   └───────────────────────────────────────────────────────────┘
 *
 * ## Why this is a component and not two copies of a div
 *
 * The two readers had drifted into entirely different surfaces: the personal
 * one grew the toolbar, the star, the chips and the footer; the unified one
 * still had a ghost `Back` button and an `<h2>`. Nothing forced them together,
 * so nothing kept them together. Both now mount this, and a change to the
 * reader's shape is one edit rather than two plus a diff nobody runs.
 *
 * ## Full bleed, and why `max-w-*` is the bug
 *
 * Both readers were `max-w-6xl mx-auto px-4 sm:px-6`. That centres a column in
 * the middle of the workspace and puts the hairline row borders in the wrong
 * place — the design runs every rule edge to edge and pads the CONTENT to 28px
 * instead. This is the same defect the compose rebuild fixed, and it is
 * invisible to a jsdom content assertion, which is why `threadReaderLayout`
 * asserts on the source of these files.
 *
 * ## One scroll region, bounded — so the route must be a fullscreen page
 *
 * The prototype sticks the toolbar to the top and the footer to the bottom of
 * one scrolling column. That is expressed here as a bounded flex column: fixed
 * bar, `flex-1 min-h-0 overflow-y-auto`, fixed bar. It only bounds if the
 * PARENT is bounded, so `/inbox/email/:commId` and `/inbox/:inboxId/:conversationId`
 * are on `RuntimeShell`'s `isFullscreenPage` list. Off that list `h-full`
 * resolves against a padded page scrollport, the column grows to its content
 * height, and the footer scrolls away — see [[runtime-shell-fullscreen-contract]].
 */
import React from 'react';
import { ChevronsRight, Star } from 'lucide-react';

/** The design's chip: one for the provider, one for the receiving mailbox. */
export function ReaderChip({ children, tone = 'neutral', title, testId }) {
    const tones = {
        neutral: 'text-[#57504B] bg-[#F1EFEA] border-[#E4E1DB]',
        warn: 'text-[#B45309] bg-[#FEF6EC] border-[#F7E4C8]',
    };
    return (
        <span
            title={title}
            data-testid={testId}
            className={`flex-shrink-0 text-[11px] font-medium leading-[16px] border
                        rounded-[5px] px-[7px] py-[2px] ${tones[tone] || tones.neutral}`}
        >
            {children}
        </span>
    );
}

export default function ThreadReaderSurface({
    /** The action bar. Rendered fixed at the top; never inside the scroll region. */
    toolbar,
    subject,
    /** Star is optional: a lane with no mail-state wiring passes no handler. */
    starred = false,
    onStar,
    starTitle,
    /** The Important marker. Absent handler ⇒ no control, as with the star. */
    important = false,
    onImportant,
    /**
     * The chips between the subject and the thread. A node rather than a data
     * prop: the two lanes genuinely show different identities (the personal
     * reader names the mailbox that received the mail, the unified one names
     * the shared inbox), and a `chips=[{…}]` prop would have to grow a `kind`
     * discriminator to tell them apart — which is the fork this component
     * exists to prevent, moved one level down.
     */
    chips,
    /** "3 messages in this thread", or null while the count is unknown. */
    threadCount,
    /** The sticky footer — `ThreadFooter`, or nothing on a read-only surface. */
    footer,
    children,
    scrollRef,
    testId,
    /** Set while the thread is loading, for `aria-busy` on the scroll region. */
    busy = false,
}) {
    return (
        <div className="flex flex-col flex-1 min-h-0 w-full bg-white" data-testid={testId}>
            {toolbar}

            <div
                ref={scrollRef}
                aria-busy={busy || undefined}
                data-testid="thread-scroll"
                className="flex-1 min-h-0 overflow-y-auto"
            >
                <div className="pt-6 pb-10">
                    <div className="px-7">
                        <div className="flex items-start gap-3">
                            <h1 className="flex-1 min-w-0 m-0 text-[21px] font-medium
                                           tracking-[-0.015em] leading-[1.35] text-[#0C0A09]
                                           break-words">
                                {subject}
                            </h1>
                            {/* Same pair, same order, as the list row:
                                star then important (#695). A flag you can set
                                in the list and not in the reader is one you
                                stop trusting. */}
                            {typeof onImportant === 'function' && (
                                <button
                                    type="button"
                                    onClick={onImportant}
                                    aria-label={important ? 'Not important' : 'Mark as important'}
                                    aria-pressed={important}
                                    title={important ? 'Not important' : 'Mark as important'}
                                    data-testid="thread-important"
                                    className="flex-shrink-0 mt-[3px] grid place-items-center
                                               w-[22px] h-[22px] rounded"
                                >
                                    <ChevronsRight
                                        className={`w-[18px] h-[18px] -rotate-45 ${
                                            important ? 'text-amber-500' : 'text-[#D6D3CD]'
                                        }`}
                                        strokeWidth={important ? 3 : 2}
                                    />
                                </button>
                            )}
                            {typeof onStar === 'function' && (
                                <button
                                    type="button"
                                    onClick={onStar}
                                    aria-label={starred ? 'Unstar' : 'Star'}
                                    aria-pressed={starred}
                                    title={starTitle || (starred ? 'Unstar' : 'Star')}
                                    data-testid="thread-star"
                                    className="flex-shrink-0 mt-[3px] grid place-items-center
                                               w-[22px] h-[22px] rounded"
                                >
                                    <Star
                                        className={`w-[19px] h-[19px] ${
                                            starred
                                                ? 'fill-[#FE9A00] text-[#FE9A00]'
                                                : 'text-[#D6D3CD]'
                                        }`}
                                    />
                                </button>
                            )}
                        </div>

                        <div className="mt-[10px] flex items-center gap-[7px]">
                            {chips}
                            <span className="flex-1" />
                            {threadCount && (
                                <span
                                    className="flex-shrink-0 text-[12px] text-[#8A837E]"
                                    data-testid="thread-message-count"
                                >
                                    {threadCount}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* The rule runs edge to edge; the content above it is padded
                        to 28px. That difference is the whole reason `max-w-*` on
                        the root is wrong — a centred column puts this rule in the
                        middle of the workspace with dead space either side. */}
                    <div className="mt-[18px] border-t border-[#EDEBE6]">{children}</div>
                </div>
            </div>

            {footer}
        </div>
    );
}
